import React, { useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import Svg, { Path, G } from 'react-native-svg';
import Animated, {
  useSharedValue,
  useAnimatedProps,
  withTiming,
  withDelay,
  withSequence,
  Easing,
} from 'react-native-reanimated';
import { AnimatedIconProps } from '../AnimatedIcon.types';
import { neutrals, icon } from 'src/theme/colors';

const AnimatedPath = Animated.createAnimatedComponent(Path);
const AnimatedView = Animated.createAnimatedComponent(View);

const PATH_DATA = 'M-85,29 L-32,83 L90,-41';
const PATH_LENGTH = 260;
const STROKE_WIDTH = 40;
const VIEWBOX_SIZE = 400;

export default function CheckmarkAnimation({
  size = 20,
  color = neutrals.white,
  backgroundColor = icon.iconSuccess,
  duration = 250,
  show = true,
  style,
}: AnimatedIconProps) {
  const checkProgressSV = useSharedValue(0);

  useEffect(() => {
    if (show) {
      checkProgressSV.value = withDelay(
        200,
        withSequence(withTiming(0.5, { duration: 0 }), withTiming(1, { duration: duration, easing: Easing.linear })),
      );
    } else {
      checkProgressSV.value = withSequence(
        withTiming(0.5, { duration: 200, easing: Easing.linear }),
        withTiming(0, { duration: 0 }),
      );
    }
  }, [show, duration]);

  const animatedPathProps = useAnimatedProps(() => {
    return {
      strokeDasharray: [PATH_LENGTH, PATH_LENGTH],
      strokeDashoffset: PATH_LENGTH * (1 - checkProgressSV.value),
      opacity: checkProgressSV.value >= 0.1 ? 1 : 0,
    };
  });

  return (
    <View style={[styles.container, style]}>
      <AnimatedView
        style={[
          styles.circle,
          {
            width: size,
            height: size,
            borderRadius: size / 2,
            backgroundColor: backgroundColor,
          },
        ]}
      >
        <Svg width={size} height={size} viewBox={`-200 -200 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}>
          <G x={-9} y={-19}>
            <AnimatedPath
              d={PATH_DATA}
              stroke={color}
              strokeWidth={STROKE_WIDTH}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="transparent"
              animatedProps={animatedPathProps}
            />
          </G>
        </Svg>
      </AnimatedView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  circle: {
    justifyContent: 'center',
    alignItems: 'center',
  },
});
